import ErrorBoundary from '@/components/ErrorBoundary';
import Layout from '@/components/Layout';
import { ThemeProvider } from '@/components/theme-provider';
import { Toaster as SonnerToaster } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, useLocation, useNavigationType } from "react-router-dom";
import './App.css';
import SignIn from "./components/auth/SignIn";
import ChatWidget from './components/chat/ChatWidget';
import { AdminBlogEditor } from './pages/admin/AdminBlogEditor';
import AdminBlogList from "./pages/admin/AdminBlogList";
import AdminDashboard from "./pages/admin/AdminDashboard";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import CityPage from "./pages/CityPage";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { initGA, trackPageView } from './utils/analytics';

/**
 * Cliente de consulta para gerenciamento de estado e cache
 * Usado pelo React Query para gerenciar requisições de dados
 */
const queryClient = new QueryClient();

/**
 * Componente AnalyticsHandler - Responsável pelo rastreamento de visualizações de página
 */
const AnalyticsHandler = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  
  useEffect(() => {
    // Initialize Google Analytics
    initGA();
  }, []);
  
  useEffect(() => {
    // Track page views on route change
    const currentPath = location.pathname + location.search;
    trackPageView(currentPath, document.title);
  }, [location]);
  
  return null;
};

/**
 * Componente App - Componente raiz da aplicação
 * 
 * Responsável por:
 * - Configurar os provedores globais (React Query, Tooltip)
 * - Definir as rotas da aplicação
 * - Configurar os componentes de notificação (Toaster, Sonner)
 * - Estabelecer o layout base da aplicação
 * - Implementar rastreamento do Google Analytics
 */
const App = () => {
  // Ensure no console logs are present

  const router = createBrowserRouter([
    // Admin routes first to avoid conflicts
    {
      path: "/admin/login",
      element: <SignIn />,
    },
    {
      path: "/admin",
      element: <AdminDashboard />,
    },
    {
      path: "/admin/blog",
      element: <AdminBlogList />,
    },
    {
      path: "/admin/blog/new",
      element: <AdminBlogEditor />,
    },
    {
      path: "/admin/blog/edit/:id",
      element: <AdminBlogEditor />,
    },
    {
      path: "/admin/blog/clone/:id",
      element: <AdminBlogEditor />,
    },
    // Blog routes
    {
      path: "/blog",
      element: (
        <Layout>
          <AnalyticsHandler />
          <Blog />
        </Layout>
      ),
    },
    {
      path: "/blog/:slug",
      element: (
        <Layout>
          <AnalyticsHandler />
          <BlogPost />
        </Layout>
      ),
    },
    // City routes - SEO-friendly format is preferred
    {
      path: "/perito-judicial-em-:city",
      element: (
        <Layout>
          <AnalyticsHandler />
          <CityPage />
        </Layout>
      ),
    },
    // Legacy city route format - kept for backward compatibility
    {
      path: "/:city",
      element: (
        <Layout>
          <AnalyticsHandler />
          <CityPage />
        </Layout>
      ),
    },
    // Home route
    {
      path: "/",
      element: (
        <Layout>
          <AnalyticsHandler />
          <Index />
        </Layout>
      ),
    },
    // Not found route
    {
      path: "*",
      element: (
        <Layout>
          <AnalyticsHandler />
          <NotFound />
        </Layout>
      ),
    }
  ]);

  return (
    <ThemeProvider>
      <ErrorBoundary fallback={<div>Something went wrong. Please try again later.</div>}>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <div className="min-h-screen w-full bg-themed-primary text-themed transition-colors duration-300">
              {/* Componentes de notificação */}
              <Toaster />
              <SonnerToaster />
              
              {/* Definição das rotas da aplicação */}
              <RouterProvider router={router} />
              {/* Vercel Analytics */}
              <Analytics />
              <SpeedInsights />
              <ChatWidget />
            </div>
          </TooltipProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
